import { Form } from 'react-final-form';
import { InputAdornment, Stack } from '@mui/material';

import { Button, Paper, SearchIcon } from '@occc/ui-design-system';
import { OnChange, Select, SelectData, TextField } from '@occc-app/design-system';

import {
  CONNECTION_STATUS_LABELS,
  CONNECTION_STATUS_OPTIONS,
  ConnectionStatus,
} from '../retailer-network-my-stores-v2-page.types';
import { hasActiveFilterOrSearch } from '../retailer-network-my-stores-v2-page.utils';

type StoreFilterBarProps = {
  stateFilter: string;
  cityFilter: string;
  connectionStatusFilter: ConnectionStatus | '';
  search: string;
  stateOptions: string[];
  cityOptions: string[];
  onStateChange: (value: string) => void;
  onCityChange: (value: string) => void;
  onConnectionStatusChange: (value: ConnectionStatus | '') => void;
  onSearchChange: (value: string) => void;
  onReset: () => void;
};

type StoreFilterFormValues = {
  state: string;
  city: string;
  connectionStatus: ConnectionStatus | '';
  search: string;
};

const SELECT_WIDTH = 135;
const SEARCH_WIDTH = 220;

const toSelectData = (options: string[], allLabel: string): SelectData[] => [
  { value: '', label: allLabel },
  ...options.map((option) => ({ value: option, label: option })),
];

const connectionStatusSelectOptions: SelectData<ConnectionStatus | ''>[] = [
  { value: '', label: 'All Statuses' },
  ...CONNECTION_STATUS_OPTIONS.map((value) => ({ value, label: CONNECTION_STATUS_LABELS[value] })),
];

export const StoreFilterBar = ({
  stateFilter,
  cityFilter,
  connectionStatusFilter,
  search,
  stateOptions,
  cityOptions,
  onStateChange,
  onCityChange,
  onConnectionStatusChange,
  onSearchChange,
  onReset,
}: StoreFilterBarProps) => (
  <Form<StoreFilterFormValues>
    onSubmit={() => undefined}
    initialValues={{
      state: stateFilter,
      city: cityFilter,
      connectionStatus: connectionStatusFilter,
      search,
    }}
    render={() => (
      <Paper elevation={1} sx={{ borderRadius: 2, p: 2 }}>
        <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap>
          <Select
            name='state'
            label='State'
            value={stateFilter}
            data={toSelectData(stateOptions, 'All States')}
            displayEmpty
            inputLabelProps={{ shrink: true }}
            onChange={(event) => onStateChange(event.target.value as string)}
            formControlProps={{ size: 'small', sx: { width: SELECT_WIDTH, flexShrink: 0 } }}
          />
          <Select
            name='city'
            label='City'
            value={cityFilter}
            data={toSelectData(cityOptions, 'All Cities')}
            displayEmpty
            disabled={!stateFilter}
            inputLabelProps={{ shrink: true }}
            onChange={(event) => onCityChange(event.target.value as string)}
            formControlProps={{ size: 'small', sx: { width: SELECT_WIDTH, flexShrink: 0 } }}
          />
          <Select
            name='connectionStatus'
            label='Connection Status'
            value={connectionStatusFilter}
            data={connectionStatusSelectOptions}
            displayEmpty
            inputLabelProps={{ shrink: true }}
            onChange={(event) =>
              onConnectionStatusChange(event.target.value as ConnectionStatus | '')
            }
            formControlProps={{ size: 'small', sx: { width: SELECT_WIDTH, flexShrink: 0 } }}
          />
          <Button
            variant='text'
            size='small'
            onClick={onReset}
            disabled={
              !hasActiveFilterOrSearch({ stateFilter, cityFilter, connectionStatusFilter, search })
            }
          >
            Reset
          </Button>
          <TextField
            name='search'
            placeholder='Search'
            size='small'
            sx={{ width: SEARCH_WIDTH, flexShrink: 0, ml: 'auto !important' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon sx={{ fontSize: 20, color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
          />
          <OnChange name='search'>{(value: string) => onSearchChange(value)}</OnChange>
        </Stack>
      </Paper>
    )}
  />
);
